import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, addMonths, isSameMonth, isSameDay, isBefore, startOfDay, parseISO, isFuture,
} from "date-fns";
import { ChevronLeft, ChevronRight, CalendarDays, XCircle, CalendarClock, CheckCircle2, Video } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { useLanguage, dfLocale } from "../i18n";

export default function GestionePrenotazione() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const loc = dfLocale[lang];
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState("view");
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [daysMap, setDaysMap] = useState({});
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/bookings/${id}`).then((r) => setBooking(r.data)).catch(() => setError(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (mode !== "reschedule" || !booking) return;
    api.get("/availability/days", { params: { month: format(cursor, "yyyy-MM"), type_id: booking.type_id } })
      .then((r) => setDaysMap(r.data.days)).catch(() => {});
  }, [mode, booking, cursor]);

  useEffect(() => {
    if (!date || !booking) return;
    setSlots(null);
    setSlot(null);
    api.get("/availability", { params: { date: format(date, "yyyy-MM-dd"), type_id: booking.type_id } })
      .then((r) => setSlots(r.data.slots)).catch(() => toast.error(t("Errore nel caricamento degli orari.")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, booking]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const manageable = booking && booking.status === "confirmed" && isFuture(parseISO(`${booking.date}T${booking.time}:00`));

  const cancel = async () => {
    if (!window.confirm(t("Vuoi davvero annullare questo appuntamento?"))) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success(t("Appuntamento annullato. Ti abbiamo inviato un'email di conferma."));
      await load();
      setMode("view");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Errore durante l'annullamento."));
    } finally {
      setBusy(false);
    }
  };

  const reschedule = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/reschedule`, { date: format(date, "yyyy-MM-dd"), time: slot });
      toast.success(t("Appuntamento spostato. Ti abbiamo inviato un'email con i nuovi dettagli."));
      await load();
      setMode("view");
      setDate(null);
      setSlot(null);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(t("Questo orario non è più disponibile. Scegline un altro."));
        const r = await api.get("/availability", { params: { date: format(date, "yyyy-MM-dd"), type_id: booking.type_id } });
        setSlots(r.data.slots);
        setSlot(null);
      } else {
        toast.error(e.response?.data?.detail || t("Errore durante lo spostamento."));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title={`${t("La tua prenotazione")} — Alsera Studio`} description={t("La tua prenotazione")} />
      <section className="pt-32 pb-24 sm:pt-40" data-testid="gestione-page">
        <div className="mx-auto max-w-2xl px-5 sm:px-8">
          {error && (
            <div className="card-soft p-10 text-center" data-testid="gestione-error">
              <h1 className="text-2xl font-bold text-antracite">{t("Prenotazione non trovata")}</h1>
              <p className="mt-3 text-antracite/65">{t("Il link potrebbe non essere valido o la prenotazione è stata rimossa.")}</p>
              <Link to="/prenota" className="btn-primary mt-6">{t("Prenota una consulenza")}</Link>
            </div>
          )}
          {!error && !booking && <p className="text-antracite/60">{t("Caricamento…")}</p>}
          {booking && (
            <Reveal>
              <div className="card-soft noise-overlay p-8 sm:p-12" data-testid="gestione-card">
                <div className="flex items-start justify-between gap-4">
                  <span className={`inline-flex rounded-full p-4 ${booking.status === "cancelled" ? "bg-red-50 text-red-600" : "bg-acqua text-petrolio"}`}>
                    {booking.status === "cancelled" ? <XCircle className="h-8 w-8" /> : <CalendarDays className="h-8 w-8" />}
                  </span>
                  <span className={`rounded-full px-3.5 py-1.5 text-xs font-bold ${booking.status === "cancelled" ? "bg-red-100 text-red-700" : booking.status === "completed" ? "bg-salvia/40 text-antracite" : "bg-acqua text-petrolio"}`} data-testid="gestione-status">
                    {booking.status === "cancelled" ? t("Annullata") : booking.status === "completed" ? t("Completata") : t("Confermata")}
                  </span>
                </div>
                <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-antracite sm:text-4xl">
                  {t("La tua prenotazione")}
                </h1>
                <dl className="mt-6 space-y-3 rounded-2xl bg-acqua/50 p-6 text-sm">
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Codice")}</dt><dd className="font-mono text-xs font-bold text-petrolio">{booking.id.slice(0, 8).toUpperCase()}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Appuntamento")}</dt><dd className="font-bold text-antracite">{t(booking.type_name)}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Data")}</dt><dd className="font-bold capitalize text-antracite" data-testid="gestione-date">{format(parseISO(booking.date), "EEEE d MMMM yyyy", { locale: loc })}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Orario")}</dt><dd className="font-bold text-antracite" data-testid="gestione-time">{booking.time} (Europe/Rome)</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Durata")}</dt><dd className="font-bold text-antracite">{booking.duration_minutes} {t("minuti")}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">Modalità</dt><dd className="flex items-center gap-1.5 font-bold text-antracite"><Video className="h-4 w-4 text-petrolio" /> Google Meet</dd></div>
                </dl>

                {booking.status === "cancelled" && (
                  <div className="mt-6 rounded-2xl bg-red-50 p-5 text-sm leading-relaxed text-red-800" data-testid="gestione-cancelled-note">
                    {t("Questo appuntamento è stato annullato. Se vuoi, puoi prenotarne uno nuovo.")}
                    <Link to="/prenota" className="btn-primary mt-4 w-full sm:w-auto" data-testid="gestione-rebook">{t("Prenota di nuovo")}</Link>
                  </div>
                )}

                {manageable && mode === "view" && (
                  <div className="mt-8 flex flex-wrap gap-4">
                    <button type="button" onClick={() => setMode("reschedule")} className="btn-primary" data-testid="gestione-reschedule-open">
                      <CalendarClock className="h-4 w-4" /> {t("Sposta appuntamento")}
                    </button>
                    <button type="button" onClick={cancel} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-7 py-3.5 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50" data-testid="gestione-cancel">
                      <XCircle className="h-4 w-4" /> {busy ? t("Attendere…") : t("Annulla appuntamento")}
                    </button>
                  </div>
                )}

                {manageable && mode === "reschedule" && (
                  <div className="mt-8" data-testid="gestione-reschedule">
                    <h2 className="text-lg font-bold text-antracite">{t("Scegli un nuovo giorno e orario")}</h2>
                    <div className="mt-5 grid gap-8 sm:grid-cols-2">
                      <div>
                        <div className="mb-3 flex items-center justify-between">
                          <button type="button" onClick={() => setCursor((c) => addMonths(c, -1))} disabled={isSameMonth(cursor, new Date())} className="rounded-full border border-salvia/50 p-2 text-petrolio hover:bg-acqua disabled:opacity-30" aria-label={t("Mese precedente")} data-testid="gestione-prev-month">
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          <p className="font-display text-sm font-bold capitalize text-antracite">{format(cursor, "MMMM yyyy", { locale: loc })}</p>
                          <button type="button" onClick={() => setCursor((c) => addMonths(c, 1))} className="rounded-full border border-salvia/50 p-2 text-petrolio hover:bg-acqua" aria-label={t("Mese successivo")} data-testid="gestione-next-month">
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center">
                          {[0, 1, 2, 3, 4, 5, 6].map((idx) => (
                            <span key={idx} className="py-1 text-[0.65rem] font-bold uppercase tracking-wider text-antracite/45">
                              {format(new Date(2024, 0, 1 + idx), "EEE", { locale: loc })}
                            </span>
                          ))}
                          {days.map((d) => {
                            const key = format(d, "yyyy-MM-dd");
                            const inMonth = isSameMonth(d, cursor);
                            const past = isBefore(d, startOfDay(new Date()));
                            const available = inMonth && !past && daysMap[key];
                            const selected = date && isSameDay(d, date);
                            return (
                              <button key={key} type="button" data-testid={`gestione-day-${key}`} disabled={!available} onClick={() => setDate(d)}
                                className={`aspect-square rounded-xl text-sm font-semibold transition-colors ${selected ? "bg-petrolio text-avorio" : available ? "bg-acqua/70 text-petrolio hover:bg-salvia/60" : "text-antracite/25"} ${!inMonth ? "opacity-30" : ""}`}>
                                {format(d, "d")}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <p className="mb-3 text-sm font-bold capitalize text-antracite">
                          {date ? format(date, "EEEE d MMMM", { locale: loc }) : t("Seleziona un giorno")}
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {slots?.filter((s) => s.available).map((s) => (
                            <button key={s.time} type="button" data-testid={`gestione-slot-${s.time.replace(":", "-")}`} onClick={() => setSlot(s.time)}
                              className={`rounded-xl px-2 py-2.5 text-sm font-bold transition-colors ${slot === s.time ? "bg-petrolio text-avorio" : "bg-acqua/70 text-petrolio hover:bg-salvia/60"}`}>
                              {s.time}
                            </button>
                          ))}
                        </div>
                        {slots && slots.every((s) => !s.available) && (
                          <p className="text-sm text-antracite/55">{t("Nessun orario disponibile in questa data.")}</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <button type="button" onClick={reschedule} disabled={!date || !slot || busy} className="btn-primary disabled:opacity-40" data-testid="gestione-reschedule-confirm">
                        <CheckCircle2 className="h-4 w-4" /> {busy ? t("Attendere…") : t("Conferma il nuovo orario")}
                      </button>
                      <button type="button" onClick={() => setMode("view")} className="btn-secondary" data-testid="gestione-reschedule-back">{t("Annulla")}</button>
                    </div>
                  </div>
                )}
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
